import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";
import ResponsiveSubPage from "../../common/core/ResponsiveSubPage";
import { useMissionTask } from "../../../providers/MissionTaskProvider";
import { MissionEnums, MissionEnumsLists, TaskEnums } from "../../../common/constants";
import { GetTaskDriverState } from "../../../common/mission";
import { Grid, Alert, AlertTitle, Button } from "@mui/material";
import TaskMapSection from "../../common/map/TaskMapSection";
import { useMissionRoute } from "../../../providers/MissionProvider";
import MissionTaskDetailsCard from "./cards/MissionTaskDetailsCard";
import MissionTaskSectionsCard from "./cards/MissionTaskSectionsCard";
import { useDevice } from "../../../providers/DeviceProvider";
import { ToggleSurface } from "../../common/core/Surface";
import NotFoundPage from "../error/NotFoundPage";
import { CompactTimeView } from "../../common/core/Time";
import config from "../../../config";
import TaskStateChip from "../../common/display/TaskStateChip";
import GoToMyMissionsDialog from "./common/GoToMyMissionsDialog";
import { NavLink } from "react-router-dom";
import { useSignal } from "@emberly/zenith-client";

const useStyles = makeStyles()(
  (theme) => ({
    mapSection: {
      height: "100%",
      borderRadius: `${theme.spacing(2)} !important`,
      "& .map": {
        borderRadius: `${theme.spacing(2)} !important`,
      }
    },
    mapSurface: {
      height: "100%",
      position: "relative"
    },
    mapCollapse: {
      "& .MuiCollapse-wrapper": {
        height: "100%"
      }
    },
    stateChip: {
      borderRadius: theme.spacing(1),
      fontSize: "14px"
    },
    timeLabel: {
      color: theme.palette.text.secondary,
      fontSize: "12px"
    },
    startNavButton: {
      position: "absolute",
      right: theme.spacing(1),
      bottom: theme.spacing(1),
      zIndex: 1,
      "& button": {
        borderRadius: `${theme.spacing(1.5)} !important`
      }
    }
  })
);

export default function MissionTaskPage() {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const { mission, missionTask, taskState, taskPath, lastTaskState, continueMissionText, driverAssignedState, continueMission, rejectTask, notFound, locked, missingVehicle } = useMissionTask();
  const missionDetails = mission.details;
  const { route, routeRevision } = useMissionRoute(taskPath);
  const { slim, isMobile } = useDevice();
  const { connecting } = useSignal();

  return notFound ? (
    <NotFoundPage backPath="../../" />
  ) : (
    <ResponsiveSubPage
      title={`${t(`mission:enums:type:${MissionEnumsLists.Type[missionDetails.type]}`)} #${mission?.number || "-"}.${missionTask?.number || "-"}`}
      backPath="../../"
      backButton
      primaryAction={driverAssignedState === TaskEnums.DriverAssignedState.Pending ? t("task:common:reject") : undefined}
      onPrimaryAction={driverAssignedState === TaskEnums.DriverAssignedState.Pending ? rejectTask : undefined}
      secondaryAction={continueMissionText}
      onSecondaryAction={continueMission}
      secondaryActionDisabled={connecting}
      primaryActionDisabled={connecting}
      maxWidth={config.pageSize.large}
    >
      <Grid item container xs={12} justifyContent="space-between" alignItems="baseline" spacing={1}>
        {
          mission?.state === MissionEnums.State.Cancelled ? (
            <Grid item xs={12}>
              <Alert variant="filled" severity="error">
                <AlertTitle>{t("task:common:cancelledTitle")}</AlertTitle>
                {t("task:common:cancelledDescription")}
              </Alert>
            </Grid>
          ) : null
        }
        <Grid item>
          <TaskStateChip
            className={classes.stateChip}
            state={taskState}
            driverAssignedState={driverAssignedState}
            label={
              <>
                {GetTaskDriverState(driverAssignedState, taskState, t, true)}
                {!!lastTaskState?.lastModified ? <span className={classes.timeLabel}>&nbsp;(<CompactTimeView refresh={Date.now()} time={lastTaskState.lastModified} />)</span> : null}
              </>
            }
          />
        </Grid>
      </Grid>

      <Grid item container xs={12} justifyContent="stretch" spacing={3}>

        {missingVehicle ? (
          <Grid item xs={12}>
            <Alert severity="warning">
              {t("task:common:missingVehicle")}
            </Alert>
          </Grid>
        ) : null}

        {!slim || taskState < TaskEnums.State.Arrived ? (
          <ToggleSurface xs={12} lg={6} className={classes.mapSurface} outerClassName={classes.mapCollapse}>

            <TaskMapSection
              locked={locked}
              className={classes.mapSection}
              routeRevision={routeRevision}
              route={route}
              path={taskPath}
              fitScreenButton
              vehicleId={missionTask?.execution?.vehicle?.id}
            >
              {
                isMobile && driverAssignedState === TaskEnums.DriverAssignedState.Accepted ? (
                  <NavLink to="./navigation" className={classes.startNavButton}>
                    <Button variant="contained" color="primary">
                      {t("task:common:startNavigation")}
                    </Button>
                  </NavLink>
                ) : null
              }
            </TaskMapSection>

          </ToggleSurface>
        ) : null}

        <Grid item container xs={12} lg={6} spacing={3}>
          <MissionTaskDetailsCard />
          {taskState >= TaskEnums.State.Arrived ? (
            <MissionTaskSectionsCard />
          ) : null}
        </Grid>

      </Grid>

      <GoToMyMissionsDialog />

    </ResponsiveSubPage>
  );
}
